import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from "rxjs/operators";
import {AuthService} from "./auth.service";
import {LeadService} from "./lead.service";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";

/**
 * Strażnik routingu (Guard) zarządzający kontrolą dostępu do modułu Programu Poleceń (Ambasador).
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik ma odpowiednie uprawnienia
 * do przeglądania statystyk afiliacyjnych i zarządzania poleceniami.
 */
@Injectable({
  providedIn: 'root'
})
export class ReferralsGuard implements CanActivate, CanLoad {
  constructor(
    private authService: AuthService,
    private leadService: LeadService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Weryfikuje uprawnienia przed fizyczną aktywacją i wyrenderowaniem interfejsu
   * modułu poleceń. Przechwytuje błędy wygaśnięcia sesji (HTTP 401), wymuszając wylogowanie
   * i przekierowanie użytkownika. Pozostałe braki autoryzacji domenowej (np. brak roli)
   * są obsługiwane przez centralną klasę narzędziową CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/referrals-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - referral - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu JavaScript
   * powiązanego z programem poleceń. Zapobiega transferowi logiki biznesowej
   * oraz detali programu afiliacyjnego do przeglądarki klienta, który nie przeszedł
   * pozytywnej weryfikacji na serwerze API.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku sukcesu autoryzacji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/referrals-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }), map(response => {
        console.log('check - referral - ok');
        return true;
      }));
  }
}
